/**
 * Mikro-Vorstart (19.10.2026) — das Mikro schon öffnen, BEVOR die Antwort zu Ende gesprochen ist.
 *
 * Befund (Journal 19.10. 06:30–07:41, `[ptt-t]`): Nach Antworten, nach denen das Mikro von selbst aufgeht
 * (`via=nach-frage`), startet es kalt: `gumEnd=+1220ms … path=cold kalt=tts-start-hifi`. getUserMedia braucht über
 * Bluetooth ~1,2 s (A2DP -> HFP), und Martins Satzanfang fehlt. Das Warmhalten des Mikros ist abgelehnt
 * (Telefon-Klang im Auto, Hi-Fi-Regel vom 28.07.). Der Ausweg: nur im Fall „danach geht das Mikro sowieso auf“ die
 * letzten ~1,2 s der Wiedergabe vorziehen.
 *
 * Dieses Modul ist die Uhr und die Messung. Es weiß NICHT, wann das Mikro aufgeht (das entscheidet app.ts über
 * `setzeVorstart().erlaubt`) und nicht, wie es geöffnet wird (ptt.ts `mikroVorstarten`).
 *
 * - `beobachteWiedergabe(quelle)`: die Schlange meldet, dass ein Stück begann. Solange das LETZTE Stück der Antwort
 *   spielt, fragt die Uhr alle 80 ms nach der Restdauer. Ist sie <= Vorlauf, fällt EINE Entscheidung je Beobachtung.
 * - `vorlaufMs()`: der Median der letzten gemessenen Mikro-Öffnungen (`gumEnd`), begrenzt auf 600–2000 ms, Vorgabe 1200.
 * - Notschalter: Einstellung `mikroVorstart` (aus = false), geprüft in `erlaubt` (app.ts).
 */

export const VORLAUF_STANDARD_MS = 1200;
export const VORLAUF_MIN_MS = 600;
export const VORLAUF_MAX_MS = 2000;
/** So viele Messungen zählen (die letzten). */
export const VORLAUF_MESSUNGEN = 9;
/** Takt der Uhr. */
export const VORSTART_TAKT_MS = 80;
const SPEICHER_SCHLUESSEL = "voice-v8.mikro-oeffnung-ms";

/** Was die Schlange über das Ende ihrer Antwort weiß. `restMs`: Restdauer des LETZTEN Stücks (null = nicht bekannt). */
export interface VorstartLage {
  restMs: number | null;
  sprecher: "schmied" | "probe" | undefined;
  endetMitFrage: boolean;
  schmiedAngesprochen: boolean;
  zusatzAngesprochen: string | null;
}

export interface VorstartQuelle {
  vorstartLage(): VorstartLage | null;
}

export interface VorstartHaken {
  /** `null` = ja, das Mikro geht nach dieser Wiedergabe auf; sonst ein Wort (kein Inhalt), warum nicht. */
  erlaubt(lage: VorstartLage): string | null;
  starte(info: { restMs: number; vorlaufMs: number }): void;
  gibFrei(grund: string): void;
  melde(zeile: string): void;
}

/** Median der Messungen, begrenzt. Ohne Messung: die Vorgabe. Rein. */
export function medianVorlauf(messungen: readonly number[]): number {
  const gut = messungen.filter((m) => Number.isFinite(m) && m > 0).slice(-VORLAUF_MESSUNGEN);
  if (gut.length === 0) return VORLAUF_STANDARD_MS;
  const s = [...gut].sort((a, b) => a - b);
  const mitte = s.length >> 1;
  const med = s.length % 2 ? s[mitte] : (s[mitte - 1] + s[mitte]) / 2;
  return Math.round(Math.min(VORLAUF_MAX_MS, Math.max(VORLAUF_MIN_MS, med)));
}

/** Ist es Zeit? Einen halben Takt Spielraum, weil die Uhr nur alle 80 ms schaut. Rein. */
export function vorstartFaellig(restMs: number | null, vorlauf: number): boolean {
  return restMs !== null && restMs > 0 && restMs <= vorlauf + VORSTART_TAKT_MS / 2;
}

let messungen: number[] = [];
let geladen = false;

function lade(): void {
  if (geladen) return;
  geladen = true;
  try {
    const roh = typeof localStorage !== "undefined" ? localStorage.getItem(SPEICHER_SCHLUESSEL) : null;
    const l: unknown = roh ? JSON.parse(roh) : [];
    if (Array.isArray(l)) messungen = l.filter((x): x is number => typeof x === "number").slice(-VORLAUF_MESSUNGEN);
  } catch {
    /* ohne Speicher gilt die Vorgabe */
  }
}

/** Ein kalter Start hat getUserMedia wirklich gebraucht: `gumEnd` (ms ab Druck). Nie von einem vorgezogenen Start. */
export function merkeMikroOeffnung(gumEndMs: number): void {
  if (!Number.isFinite(gumEndMs) || gumEndMs <= 0) return;
  lade();
  messungen.push(Math.round(gumEndMs));
  messungen = messungen.slice(-VORLAUF_MESSUNGEN);
  try {
    if (typeof localStorage !== "undefined") localStorage.setItem(SPEICHER_SCHLUESSEL, JSON.stringify(messungen));
  } catch {
    /* egal */
  }
}

export function vorlaufMs(): number {
  lade();
  return medianVorlauf(messungen);
}

let haken: VorstartHaken | null = null;
let uhr: ReturnType<typeof setTimeout> | null = null;
let beobachtet: VorstartQuelle | null = null;
let entschieden = false;
/** Die letzte Absage (für die Zeile `[ptt-t]`): Wort und Zeitpunkt. */
let letzte: { grund: string; um: number } | null = null;

export function setzeVorstart(h: VorstartHaken | null): void {
  haken = h;
}

/** Warum es zuletzt kein Vorstart war (jünger als `maxAlterMs`), sonst null. Nur ein Wort. */
export function letzterVorstartGrund(maxAlterMs = 30_000): string | null {
  return letzte && Date.now() - letzte.um <= maxAlterMs ? letzte.grund : null;
}

function takt(): void {
  uhr = null;
  const q = beobachtet;
  if (!q || !haken || entschieden) return;
  let lage: VorstartLage | null = null;
  try {
    lage = q.vorstartLage();
  } catch {
    lage = null;
  }
  const vorlauf = vorlaufMs();
  if (lage && vorstartFaellig(lage.restMs, vorlauf)) {
    entschieden = true;
    const rest = Math.round(lage.restMs as number);
    let grund: string | null;
    try {
      grund = haken.erlaubt(lage);
    } catch {
      grund = "fehler";
    }
    if (grund) {
      letzte = { grund, um: Date.now() };
      haken.melde(`[ptt] vorstart nein grund=${grund} restMs=${rest}`);
      return;
    }
    letzte = null;
    haken.melde(`[ptt] vorstart ja restMs=${rest} vorlaufMs=${vorlauf}`);
    haken.starte({ restMs: rest, vorlaufMs: vorlauf });
    return;
  }
  uhr = setTimeout(takt, VORSTART_TAKT_MS);
}

/** Die Schlange spielt ein neues Stück: ab jetzt beobachten (ersetzt eine frühere Beobachtung). */
export function beobachteWiedergabe(quelle: VorstartQuelle): void {
  if (!haken) return;
  if (beobachtet !== quelle) entschieden = false;
  beobachtet = quelle;
  if (uhr === null && !entschieden) uhr = setTimeout(takt, VORSTART_TAKT_MS);
}

/** Die Wiedergabe ist aus (Ende oder Abbruch): die Uhr steht. Das Mikro gibt `vorstartAbbruch` frei, nicht diese Funktion. */
export function beendeBeobachtung(quelle?: VorstartQuelle): void {
  if (quelle && beobachtet !== quelle) return;
  if (uhr !== null) clearTimeout(uhr);
  uhr = null;
  beobachtet = null;
  entschieden = false;
}

/** Abbruch (Barge-in, neuer Beitrag, Stopp): die Uhr steht UND ein vorgezogenes Mikro wird wieder freigegeben. */
export function vorstartAbbruch(grund: string, quelle?: VorstartQuelle): void {
  if (quelle && beobachtet !== quelle) return;
  beendeBeobachtung();
  try {
    haken?.gibFrei(grund);
  } catch {
    /* die Freigabe darf nie werfen */
  }
}

/** Nur für Tests: alles auf Anfang. */
export function vorstartZuruecksetzen(): void {
  beendeBeobachtung();
  haken = null;
  messungen = [];
  geladen = true;
  letzte = null;
}
